// src/data/hardnessBaseline.js — s136 (DECISIONS #58).
// The one hardness number a planner tool works from, and how to say where it came from.
// Order: the city utility's PUBLISHED figure → the median of what the state's covered
// utilities publish (statePublishedHardness.js) → the planning waterProfile midpoint,
// labelled as a planning estimate. The old tools used `city.hardness` (a planning value,
// #22) and printed it as "N mg/L from <utility>", which credited an estimate to a utility.
import { STATE_PUBLISHED_HARDNESS } from './statePublishedHardness.js';
import { hardnessBandValue, hardnessFigure, hardnessSourcing } from './water.js';

export function hardnessBaseline(stateRecord, city) {
  const name = stateRecord?.name || '';
  const p = STATE_PUBLISHED_HARDNESS[stateRecord?.slug];
  const s = city ? hardnessSourcing(city) : null;
  if (city && s && (s.shape === 'point' || s.shape === 'range')) {
    const v = hardnessBandValue(city);
    if (v != null) {
      const who = city.utility || `the ${city.name} municipal supply`;
      return { mgL: Math.round(v), basis: 'city', title: `${hardnessFigure(city, { withUnit: true })} published by ${who}`, short: `${Math.round(v)} mg/L (${city.name}'s published figure)` };
    }
  }
  if (p && p.n) {
    const span = p.lo === p.hi ? `${p.lo} mg/L` : `${p.lo}–${p.hi} mg/L`;
    const title = city
      ? `${city.utility || city.name} publishes no hardness figure · working from ${p.med} mg/L, the median of ${p.n} ${name} ${p.n === 1 ? 'utility' : 'utilities'} that do`
      : `${span} published across ${p.n} ${name} ${p.n === 1 ? 'utility' : 'utilities'} · median ${p.med} mg/L`;
    return { mgL: p.med, basis: city ? 'city-unpublished' : 'state', title, short: `${p.med} mg/L (median of published ${name} figures)` };
  }
  const wp = stateRecord?.waterProfile || {};
  const mid = Math.round((wp.hardnessLow + wp.hardnessHigh) / 2);
  return { mgL: mid, basis: 'planning', title: `${wp.hardnessLow}–${wp.hardnessHigh} mg/L statewide · planning estimate, no covered ${name} utility publishes a figure`, short: `${mid} mg/L (planning estimate)` };
}

// A state's headline range: what its covered utilities publish, else the planning
// range with pub:false so the caller labels it PLANNING. `tier` is the band of the
// published median (or the planning midpoint) — never the hand-kept dominantTier.
export function stateRange(slug, planLo, planHi) {
  const p = STATE_PUBLISHED_HARDNESS[slug];
  if (p && p.n) return { pub: true, lo: p.lo, hi: p.hi, med: p.med, n: p.n, of: p.of };
  if (planLo == null || planHi == null) return { pub: false, lo: null, hi: null, med: null, n: 0, of: p?.of ?? 0 };
  return { pub: false, lo: planLo, hi: planHi, med: Math.round((planLo + planHi) / 2), n: 0, of: p?.of ?? 0 };
}
export const fmtRange = (r) => (r.lo == null ? '' : r.lo === r.hi ? `${r.lo}` : `${r.lo}–${r.hi}`);

// s137 (#63): the key a planner uses for ?city= and its <select> values. Covered cities carry their real
// page slug (topCities[].citySlug, stamped by scripts/build-state-tiers.mjs); other listed places fall back
// to a name slug with qualifiers and dots dropped ("Wasilla-Palmer (Mat-Su)" → wasilla-palmer).
export const cityKey = (c) => c.citySlug || c.name.replace(/\s*\([^)]*\)/g, '').replace(/\./g, '').trim().toLowerCase().replace(/\s+/g, '-');
